<template>
	<div class="number-stepper" :class="{ disabled }">
		<Button
			link
			class="stepper-button"
			:disabled="disabled || atMin"
			@mousedown.prevent
			@click="nudge(-step)"
		>
			<MinusIcon type="regular" />
		</Button>
		<input
			class="stepper-input"
			type="text"
			inputmode="numeric"
			:value="draft"
			:disabled="disabled"
			@input="draft = $event.target.value"
			@blur="commitDraft"
			@keyup.enter="commitDraft"
		/>
		<Button
			link
			class="stepper-button"
			:disabled="disabled || atMax"
			@mousedown.prevent
			@click="nudge(step)"
		>
			<PlusIcon type="regular" />
		</Button>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import Button from './Button'
import { MinusIcon, PlusIcon } from '@/icons'

const props = defineProps({
	modelValue: { type: Number, default: 0 },
	min: { type: Number, default: null },
	max: { type: Number, default: null },
	step: { type: Number, default: 1 },
	disabled: { type: Boolean, default: null },
})

// `commit` is deliberately separate from `update:modelValue`: it only fires on
// a settled value (a button, blur or Enter), so a consumer can save on it
// without writing once per keystroke.
const emit = defineEmits(['update:modelValue', 'commit'])

const draft = ref(String(props.modelValue ?? 0))

// The parent owns the value — a rejected save resets modelValue back to the
// stored setting, which has to win over whatever is sitting in the field.
watch(
	() => props.modelValue,
	(value) => (draft.value = String(value ?? 0))
)

const clamp = (value) => {
	let next = value
	if (props.min !== null && next < props.min) next = props.min
	if (props.max !== null && next > props.max) next = props.max
	return next
}

// What's on screen wins over the last saved value, so stepping after typing
// steps from what was typed. An emptied or unparseable field falls back to the
// saved value rather than to a bound — the server only takes an integer, and
// -32768 is not what someone clearing the box meant.
const workingValue = computed(() => {
	const parsed = Number.parseInt(draft.value, 10)
	return clamp(Number.isNaN(parsed) ? props.modelValue ?? 0 : parsed)
})

const atMin = computed(
	() => props.min !== null && workingValue.value <= props.min
)
const atMax = computed(
	() => props.max !== null && workingValue.value >= props.max
)

const commit = (value) => {
	const next = clamp(value)
	// Normalize what's on screen even when the value didn't move, so '007' or a
	// cleared field doesn't stay there
	draft.value = String(next)
	if (next === props.modelValue) return
	emit('update:modelValue', next)
	emit('commit', next)
}

const commitDraft = () => commit(workingValue.value)

const nudge = (delta) => commit(workingValue.value + delta)
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.number-stepper {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	flex-shrink: 0;
	height: 28px;
	padding: 0 4px;
	border: 1px solid var(--neutral-200);
	border-radius: 14px;
	background: var(--neutral-0);

	&.disabled {
		opacity: 0.6;
	}

	.stepper-button {
		padding: 0;
		height: 20px;
		width: 20px;
		min-width: 20px;

		.pronto-icon {
			font-size: 10px;
		}

		// link Buttons render as <a>, so disabled is a class, not the attribute
		&.disabled {
			color: var(--neutral-300);
			opacity: 1;
		}
	}

	.stepper-input {
		width: 40px;
		border: none;
		outline: none;
		background: transparent;
		text-align: center;
		font-family: Avenir-Heavy;
		font-size: 14px;
		color: var(--neutral-800);

		&:disabled {
			cursor: not-allowed;
		}
	}
}
</style>
